import {Inject, Injectable} from '@angular/core';
import {Meta, Title} from "@angular/platform-browser";
import {DOCUMENT} from "@angular/common";
import {Router} from "@angular/router";
import {catchError, forkJoin, Observable, of, shareReplay} from "rxjs";
import {map} from "rxjs/operators";
import {PageMetaInterface} from "../interfaces/meta.interface";
import {PublicService} from "./public.service";
import {environment} from "../../../../../../environments/environment";
import {resizeImage} from "../pipes/image-resize.pipe";
import {FileSnippet} from "../../../../../theme/admin/interfaces/file-snippet.interface";

export const SITE_NAME = 'Dr. Alergolog';
export const DEFAULT_LANGUAGE = 'ro';
export const SUPPORTED_LANGUAGES = ['ro', 'ru', 'en'];

const DEFAULT_IMAGE = '/assets/images/meta/meta.png';
const DEFAULT_TITLE = 'Dr. Alergolog – Alergolog-imunolog Alina Istrate, Chișinău';
const DEFAULT_DESCRIPTION = 'Consultații la medicul alergolog-imunolog Dr. Alina Istrate în Chișinău și online: dermatită atopică, alergii alimentare, urticarie, astm și imunitate.';
const DEFAULT_KEYWORDS = 'alergolog, imunolog, alergolog Chișinău, Dr. Alina Istrate, dermatită atopică, alergii alimentare, urticarie, imunitate';
const ROBOTS_INDEX = 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';
const ROBOTS_NOINDEX = 'noindex, follow';
const TITLE_MAX = 60;
const DESCRIPTION_MAX = 160;
const JSON_LD_ID = 'app-structured-data';

const OG_LOCALES: Record<string, string> = {ro: 'ro_RO', ru: 'ru_RU', en: 'en_US'};

const DAY_MAP: Record<string, string> = {
  luni: 'Monday',
  marti: 'Tuesday',
  miercuri: 'Wednesday',
  joi: 'Thursday',
  vineri: 'Friday',
  sambata: 'Saturday',
  duminica: 'Sunday'
};
const DAY_ORDER = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export type Localized = string | { [language: string]: string | null } | null | undefined;
export type SeoImage = string | FileSnippet[] | FileSnippet | null | undefined;

export interface SeoBreadcrumb {
  name: Localized;
  path: string;
}

export interface SeoPage {
  path: string;
  title?: Localized;
  description?: Localized;
  keywords?: Localized;
  image?: SeoImage;
  imageAlt?: Localized;
  meta?: PageMetaInterface | PageMetaInterface[] | null;
  pageMeta?: boolean;
  type?: 'website' | 'article' | 'profile';
  pageType?: string;
  noindex?: boolean;
  breadcrumbs?: SeoBreadcrumb[];
  schema?: Record<string, any>[];
  mainEntity?: string;
  publishedTime?: string | null;
  modifiedTime?: string | null;
}

interface SiteData {
  company: any;
  about: any;
  networks: any[];
  languages: string[];
}

@Injectable({
  providedIn: 'root'
})
export class MetaService {
  private page: SeoPage | null = null;
  private adminMeta: PageMetaInterface | null = null;
  private site: SiteData | null = null;
  private site$?: Observable<SiteData>;
  private pageMeta$?: Observable<any[]>;
  private pageToken = 0;

  constructor(private meta: Meta,
              private title: Title,
              private publicService: PublicService,
              private router: Router,
              @Inject(DOCUMENT) private document: Document) {
  }

  get origin(): string {
    return (environment.appUrl || '').replace(/\/+$/, '');
  }

  get language(): string {
    const segment = (this.router.url || '').split(/[?#]/)[0].split('/')[1];
    return SUPPORTED_LANGUAGES.includes(segment) ? segment : DEFAULT_LANGUAGE;
  }

  setPage(page: SeoPage): void {
    const token = ++this.pageToken;
    this.page = page;
    this.adminMeta = this.firstMeta(page.meta);
    this.render();

    this.loadSite().subscribe(site => {
      if (token !== this.pageToken) return;
      this.site = site;
      this.render();
    });

    if (page.pageMeta) {
      this.loadPageMeta().subscribe(rows => {
        if (token !== this.pageToken) return;
        const row = this.matchPageMeta(rows, page.path);
        if (row) {
          this.adminMeta = this.mergeMeta(this.firstMeta(row.meta), this.adminMeta);
          this.render();
        }
      });
    }
  }

  setNotFound(path: string): void {
    this.setPage({
      path,
      title: 'Pagina nu a fost găsită',
      description: 'Pagina căutată nu există sau a fost mutată. Descoperă consultațiile, cursurile și ghidurile Dr. Alergolog.',
      noindex: true
    });
  }

  setPrivate(): void {
    this.pageToken++;
    this.page = null;
    this.title.setTitle(`Cont | ${SITE_NAME}`);
    this.setName('robots', 'noindex, nofollow');
    this.setName('googlebot', 'noindex, nofollow');
    this.document.head.querySelector("link[rel='canonical']")?.remove();
    this.document.head.querySelectorAll("link[rel='alternate'][hreflang]").forEach(link => link.remove());
    this.document.getElementById(JSON_LD_ID)?.remove();
  }

  isPrivatePath(url: string): boolean {
    return /^\/((ro|ru|en)\/)?(admin|superadmin|student|auth)(\/|$)/.test((url || '').split(/[?#]/)[0]);
  }

  url(path: string, language: string = this.language): string {
    const clean = this.normalizePath(path);
    const prefix = language && language !== DEFAULT_LANGUAGE ? `/${language}` : '';
    const full = `${prefix}${clean === '/' ? '' : clean}`;
    return `${this.origin}${full || '/'}`;
  }

  text(value: Localized, language: string = this.language): string {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value.trim();
    const preferred = value[language];
    if (preferred && String(preferred).trim()) return String(preferred).trim();
    const fallback = value[DEFAULT_LANGUAGE];
    if (fallback && String(fallback).trim()) return String(fallback).trim();
    const first = Object.values(value).find(item => item && String(item).trim());
    return first ? String(first).trim() : '';
  }

  plain(value: Localized, language: string = this.language): string {
    const html = this.text(value, language);
    if (!html) return '';
    const segments = html
      .split(/<br\s*\/?>|<\/p>|<\/li>|<\/h[1-6]>|<\/div>|\n/i)
      .map(part => this.decode(part.replace(/<[^>]*>/g, ' ')).replace(/\s+/g, ' ').replace(/^[-–•*]+\s*/, '').trim())
      .filter(Boolean);
    return segments.reduce((result, segment) => {
      if (!result) return segment;
      const last = result.charAt(result.length - 1);
      if ('.!?:;…'.includes(last)) return `${result} ${segment}`;
      return `${result}. ${segment.charAt(0).toUpperCase()}${segment.slice(1)}`;
    }, '');
  }

  truncate(value: string, max: number): string {
    const text = (value || '').trim();
    if (text.length <= max) return text;
    let cut = text.slice(0, max - 1);
    const space = cut.lastIndexOf(' ');
    if (space > max / 2) cut = cut.slice(0, space);
    return `${cut.replace(/[\s,;:.\-–]+$/, '')}…`;
  }

  imageUrl(image: SeoImage): string {
    if (!image) return '';
    if (typeof image === 'string') return image.trim();
    if (Array.isArray(image)) return image[0]?.file_url || '';
    return image.file_url || '';
  }

  absolute(url: string): string {
    if (!url) return '';
    if (/^https?:\/\//i.test(url)) return url;
    return `${this.origin}${url.startsWith('/') ? '' : '/'}${url}`;
  }

  organizationId(): string {
    return `${this.origin}/#organization`;
  }

  physicianId(): string {
    return `${this.origin}/#physician`;
  }

  breadcrumbLabel(key: string): string {
    const labels: Record<string, string> = {
      home: 'Acasă',
      services: 'Consultații',
      courses: 'Cursuri',
      events: 'Evenimente',
      guides: 'Ghiduri',
      blogs: 'Articole',
      about: 'Despre',
      contact: 'Contacte',
      book: 'Carte'
    };
    return labels[key] || key;
  }

  eventStartDate(date: string | null | undefined, time: string | null | undefined): string {
    if (!date) return '';
    const parsed = new Date(date);
    if (isNaN(parsed.getTime())) return '';
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Europe/Chisinau',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(parsed).reduce((acc: Record<string, string>, part) => {
      acc[part.type] = part.value;
      return acc;
    }, {});
    const day = `${parts['year']}-${parts['month']}-${parts['day']}`;
    const match = (time || '').match(/(\d{1,2})[:.](\d{2})/);
    if (!match) return day;
    const hours = match[1].padStart(2, '0');
    const minutes = match[2];
    const local = new Date(`${day}T${hours}:${minutes}:00Z`);
    const zoned = new Date(local.toLocaleString('en-US', {timeZone: 'Europe/Chisinau'}));
    const utc = new Date(local.toLocaleString('en-US', {timeZone: 'UTC'}));
    const offsetMinutes = Math.round((zoned.getTime() - utc.getTime()) / 60000);
    const sign = offsetMinutes >= 0 ? '+' : '-';
    const abs = Math.abs(offsetMinutes);
    const offset = `${sign}${String(Math.floor(abs / 60)).padStart(2, '0')}:${String(abs % 60).padStart(2, '0')}`;
    return `${day}T${hours}:${minutes}:00${offset}`;
  }

  itemList(path: string, items: { name: Localized; path: string; image?: SeoImage }[]): Record<string, any> {
    return {
      '@type': 'ItemList',
      '@id': `${this.url(path)}#list`,
      numberOfItems: items.length,
      itemListElement: items.map((item, index) => {
        const image = this.imageUrl(item.image);
        return {
          '@type': 'ListItem',
          position: index + 1,
          name: this.plain(item.name),
          url: this.url(item.path),
          ...(image ? {image: this.absolute(image)} : {})
        };
      })
    };
  }

  price(value: string | number | null | undefined): string {
    const match = String(value ?? '').replace(/\s/g, '').replace(',', '.').match(/\d+(\.\d+)?/);
    return match ? match[0] : '';
  }

  private render(): void {
    const page = this.page;
    if (!page) return;

    const language = this.language;
    const path = this.normalizePath(page.path);
    const canonical = this.url(path, language);
    const admin = this.adminMeta;

    const baseTitle = this.plain(page.title, language);
    const adminTitle = this.usable(this.plain(admin?.title as Localized, language));
    const title = adminTitle || this.composeTitle(baseTitle);

    const adminDescription = this.usable(this.plain(admin?.description as Localized, language));
    const description = this.truncate(adminDescription || this.plain(page.description, language) || DEFAULT_DESCRIPTION, DESCRIPTION_MAX);

    const adminKeywords = this.usable(this.plain(admin?.keywords as Localized, language));
    const keywords = adminKeywords || this.plain(page.keywords, language) || this.composeKeywords(baseTitle);

    const adminImage = this.imageUrl(admin?.image as SeoImage);
    const rawImage = adminImage || this.imageUrl(page.image) || DEFAULT_IMAGE;
    const absoluteImage = this.absolute(rawImage);
    const ogImage = /^https?:\/\//i.test(rawImage) && !rawImage.startsWith(this.origin)
      ? resizeImage(absoluteImage, {format: 'jpg', width: 1200, quality: 85})
      : absoluteImage;
    const imageAlt = this.plain(page.imageAlt, language) || baseTitle || title;
    const robots = page.noindex ? ROBOTS_NOINDEX : ROBOTS_INDEX;
    const ogType = page.type || 'website';
    const locale = OG_LOCALES[language] || OG_LOCALES[DEFAULT_LANGUAGE];

    this.document.documentElement?.setAttribute('lang', language);
    this.title.setTitle(title);

    this.setName('title', title);
    this.setName('description', description);
    this.setName('keywords', keywords);
    this.setName('robots', robots);
    this.setName('googlebot', robots);
    this.setName('author', 'Dr. Alina Istrate');
    this.setName('copyright', this.usable(this.plain(admin?.copyright as Localized, language)) || SITE_NAME);

    this.setProperty('og:type', ogType);
    this.setProperty('og:site_name', SITE_NAME);
    this.setProperty('og:locale', locale);
    this.setProperty('og:title', title);
    this.setProperty('og:description', description);
    this.setProperty('og:url', canonical);
    this.setProperty('og:image', ogImage);
    this.setProperty('og:image:secure_url', ogImage);
    this.setProperty('og:image:alt', imageAlt);
    this.setProperty('og:image:type', /\.png($|\?)/i.test(ogImage) ? 'image/png' : 'image/jpeg');

    if (ogType === 'article' && page.publishedTime) {
      this.setProperty('article:published_time', page.publishedTime);
    } else {
      this.meta.removeTag("property='article:published_time'");
    }
    if (ogType === 'article' && page.modifiedTime) {
      this.setProperty('article:modified_time', page.modifiedTime);
    } else {
      this.meta.removeTag("property='article:modified_time'");
    }

    this.setName('twitter:card', 'summary_large_image');
    this.setName('twitter:title', title);
    this.setName('twitter:description', description);
    this.setName('twitter:image', ogImage);
    this.setName('twitter:image:alt', imageAlt);
    this.setName('twitter:url', canonical);

    this.setCanonical(canonical);
    this.setAlternates(path, page.noindex);
    this.setJsonLd(this.buildGraph(page, {
      language,
      canonical,
      title,
      description,
      image: absoluteImage,
      path
    }));
  }

  private buildGraph(page: SeoPage, ctx: { language: string; canonical: string; title: string; description: string; image: string; path: string }): Record<string, any> {
    const graph: Record<string, any>[] = [];
    const site = this.site;
    const company = site?.company;
    const about = site?.about;
    const sameAs = (site?.networks || [])
      .map(network => network?.link)
      .filter((link: string) => /^https?:\/\//i.test(link || ''));

    const logo = this.imageUrl(company?.logo) || `${this.origin}/assets/images/meta/favicon.svg`;
    const phone = (company?.phone_number || company?.hotline || '').trim();
    const address = this.postalAddress(company?.address);
    const openingHours = this.openingHours(company?.schedule);
    const physicianName = (about?.name || 'Alina Istrate').trim();
    const physicianImage = this.imageUrl(about?.image);

    const organization: Record<string, any> = {
      '@type': ['Physician', 'MedicalBusiness'],
      '@id': this.organizationId(),
      name: (company?.name || SITE_NAME).trim(),
      alternateName: 'Platforma Alergolog',
      url: `${this.origin}/`,
      logo: this.absolute(logo),
      image: this.absolute(physicianImage || DEFAULT_IMAGE),
      description: DEFAULT_DESCRIPTION,
      address,
      areaServed: {'@type': 'Country', name: 'Republica Moldova'},
      knowsAbout: ['Alergologie', 'Imunologie clinică', 'Dermatită atopică', 'Alergii alimentare', 'Urticarie', 'Astm bronșic', 'Vaccinare'],
      founder: {'@id': this.physicianId()},
      employee: {'@id': this.physicianId()}
    };
    if (phone) organization['telephone'] = phone;
    if (company?.email) organization['email'] = company.email.trim();
    if (openingHours.length) organization['openingHoursSpecification'] = openingHours;
    if (sameAs.length) organization['sameAs'] = sameAs;
    if (phone || company?.email) {
      organization['contactPoint'] = {
        '@type': 'ContactPoint',
        contactType: 'customer service',
        ...(phone ? {telephone: phone} : {}),
        ...(company?.email ? {email: company.email.trim()} : {}),
        availableLanguage: ['ro', 'ru']
      };
    }
    graph.push(organization);

    const physician: Record<string, any> = {
      '@type': 'Person',
      '@id': this.physicianId(),
      name: `Dr. ${physicianName}`,
      jobTitle: 'Medic alergolog-imunolog',
      url: this.url('/about-us', ctx.language),
      worksFor: {'@id': this.organizationId()},
      knowsAbout: ['Alergologie', 'Imunologie clinică']
    };
    if (physicianImage) physician['image'] = this.absolute(physicianImage);
    const physicianDescription = this.truncate(this.plain(about?.description, ctx.language), 300);
    if (physicianDescription) physician['description'] = physicianDescription;
    if (sameAs.length) physician['sameAs'] = sameAs;
    graph.push(physician);

    graph.push({
      '@type': 'WebSite',
      '@id': `${this.origin}/#website`,
      url: `${this.origin}/`,
      name: SITE_NAME,
      alternateName: 'Platforma Alergolog',
      inLanguage: ctx.language,
      publisher: {'@id': this.organizationId()}
    });

    const breadcrumbs = this.breadcrumbItems(page.breadcrumbs, ctx.language);
    const webPage: Record<string, any> = {
      '@type': page.pageType || 'WebPage',
      '@id': `${ctx.canonical}#webpage`,
      url: ctx.canonical,
      name: ctx.title,
      description: ctx.description,
      inLanguage: ctx.language,
      isPartOf: {'@id': `${this.origin}/#website`},
      about: {'@id': this.organizationId()},
      primaryImageOfPage: {'@type': 'ImageObject', url: ctx.image}
    };
    if (page.mainEntity) webPage['mainEntity'] = {'@id': page.mainEntity};
    if (page.publishedTime) webPage['datePublished'] = page.publishedTime;
    if (page.modifiedTime) webPage['dateModified'] = page.modifiedTime;
    if (breadcrumbs) webPage['breadcrumb'] = {'@id': `${ctx.canonical}#breadcrumb`};
    graph.push(webPage);

    if (breadcrumbs) {
      graph.push({
        '@type': 'BreadcrumbList',
        '@id': `${ctx.canonical}#breadcrumb`,
        itemListElement: breadcrumbs
      });
    }

    (page.schema || []).filter(Boolean).forEach(node => graph.push(node));

    return {'@context': 'https://schema.org', '@graph': graph};
  }

  private breadcrumbItems(items: SeoBreadcrumb[] | undefined, language: string): Record<string, any>[] | null {
    if (!items?.length) return null;
    const list = [{name: this.breadcrumbLabel('home'), path: '/'}, ...items];
    return list.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: this.plain(item.name, language) || this.breadcrumbLabel('home'),
      item: this.url(item.path, language)
    }));
  }

  private postalAddress(address: string | null | undefined): Record<string, any> {
    const parts = (address || 'Chișinău, Republica Moldova').split(',').map(part => part.trim()).filter(Boolean);
    const result: Record<string, any> = {'@type': 'PostalAddress', addressCountry: 'MD'};
    const countryIndex = parts.findIndex(part => /moldova/i.test(part));
    const local = countryIndex >= 0 ? parts.slice(0, countryIndex) : parts;
    if (local.length === 1) {
      result['addressLocality'] = local[0];
    } else if (local.length > 1) {
      result['addressLocality'] = local[local.length - 1];
      result['streetAddress'] = local.slice(0, -1).join(', ');
    } else {
      result['addressLocality'] = 'Chișinău';
    }
    return result;
  }

  private openingHours(schedule: Localized): Record<string, any>[] {
    const text = this.plain(schedule, DEFAULT_LANGUAGE)
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase();
    if (!text) return [];
    const result: Record<string, any>[] = [];
    const covered = new Set<string>();
    const pattern = /(luni|marti|miercuri|joi|vineri|sambata|duminica)\s*(?:[-–]\s*(luni|marti|miercuri|joi|vineri|sambata|duminica))?\s*:?\s*(\d{1,2}[:.]\d{2})\s*[-–]\s*(\d{1,2}[:.]\d{2})/g;
    let match: RegExpExecArray | null;
    while ((match = pattern.exec(text)) !== null) {
      const start = DAY_ORDER.indexOf(DAY_MAP[match[1]]);
      const end = match[2] ? DAY_ORDER.indexOf(DAY_MAP[match[2]]) : start;
      if (start < 0 || end < 0) continue;
      const days: string[] = [];
      for (let index = start; ; index = (index + 1) % 7) {
        if (!covered.has(DAY_ORDER[index])) days.push(DAY_ORDER[index]);
        if (index === end || days.length === 7) break;
      }
      if (!days.length) continue;
      days.forEach(day => covered.add(day));
      result.push({
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: days,
        opens: match[3].replace('.', ':').padStart(5, '0'),
        closes: match[4].replace('.', ':').padStart(5, '0')
      });
    }
    return result;
  }

  private setAlternates(path: string, noindex?: boolean): void {
    this.document.head.querySelectorAll("link[rel='alternate'][hreflang]").forEach(link => link.remove());
    if (noindex) return;
    const languages = this.site?.languages?.length ? this.site.languages : [DEFAULT_LANGUAGE];
    languages.forEach(language => this.appendLink('alternate', this.url(path, language), language));
    this.appendLink('alternate', this.url(path, DEFAULT_LANGUAGE), 'x-default');
  }

  private appendLink(rel: string, href: string, hreflang: string): void {
    const link = this.document.createElement('link');
    link.setAttribute('rel', rel);
    link.setAttribute('hreflang', hreflang);
    link.setAttribute('href', href);
    this.document.head.appendChild(link);
  }

  private setCanonical(url: string): void {
    let link = this.document.head.querySelector<HTMLLinkElement>("link[rel='canonical']");
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }
    link.setAttribute('href', url);
  }

  private setJsonLd(data: Record<string, any>): void {
    let script = this.document.getElementById(JSON_LD_ID) as HTMLScriptElement | null;
    if (!script) {
      script = this.document.createElement('script');
      script.setAttribute('type', 'application/ld+json');
      script.setAttribute('id', JSON_LD_ID);
      this.document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(data)
      .replace(/</g, '\\u003c')
      .replace(/>/g, '\\u003e')
      .replace(/&/g, '\\u0026');
  }

  private setName(name: string, content: string): void {
    if (content) {
      this.meta.updateTag({name, content});
    } else {
      this.meta.removeTag(`name='${name}'`);
    }
  }

  private setProperty(property: string, content: string): void {
    if (content) {
      this.meta.updateTag({property, content});
    } else {
      this.meta.removeTag(`property='${property}'`);
    }
  }

  private composeTitle(title: string): string {
    const clean = (title || '').trim();
    if (!clean) return DEFAULT_TITLE;
    if (clean.includes(SITE_NAME)) return clean;
    const suffix = ` | ${SITE_NAME}`;
    if (clean.length + suffix.length <= TITLE_MAX) return `${clean}${suffix}`;
    const withoutBrackets = clean.replace(/\s*\([^)]*\)/g, '').trim();
    if (withoutBrackets && withoutBrackets.length + suffix.length <= TITLE_MAX) return `${withoutBrackets}${suffix}`;
    if (clean.length <= TITLE_MAX) return clean;
    return this.truncate(withoutBrackets || clean, TITLE_MAX);
  }

  private composeKeywords(title: string): string {
    const clean = (title || '').toLowerCase().replace(/[„”"«»]/g, '').trim();
    return clean ? `${clean}, ${DEFAULT_KEYWORDS}` : DEFAULT_KEYWORDS;
  }

  private usable(value: string): string {
    const clean = (value || '').trim();
    if (clean.length < 3) return '';
    if (/^(test|testing|title|description|keywords|meta|null|undefined|-+|\.+)\d*$/i.test(clean)) return '';
    return clean;
  }

  private firstMeta(meta: PageMetaInterface | PageMetaInterface[] | null | undefined): PageMetaInterface | null {
    if (!meta) return null;
    return Array.isArray(meta) ? (meta[0] || null) : meta;
  }

  private mergeMeta(primary: PageMetaInterface | null, fallback: PageMetaInterface | null): PageMetaInterface | null {
    if (!primary) return fallback;
    if (!fallback) return primary;
    const language = this.language;
    const pick = (key: keyof PageMetaInterface) => this.usable(this.plain(primary[key] as Localized, language)) ? primary[key] : fallback[key];
    return {
      ...fallback,
      title: pick('title'),
      description: pick('description'),
      keywords: pick('keywords'),
      copyright: pick('copyright'),
      image: this.imageUrl(primary.image as SeoImage) ? primary.image : fallback.image
    } as PageMetaInterface;
  }

  private matchPageMeta(rows: any[], path: string): any {
    const target = this.normalizePath(path);
    const language = this.language;
    return (rows || []).find(row => {
      if (row?.active === false) return false;
      const value = this.text(row?.site_url_path, language);
      return value && this.normalizePath(value) === target;
    });
  }

  private normalizePath(path: string): string {
    let clean = (path || '/').split(/[?#]/)[0].trim();
    if (/^https?:\/\//i.test(clean)) {
      clean = clean.replace(/^https?:\/\/[^/]+/i, '');
    }
    const segments = clean.split('/').filter(Boolean);
    if (segments.length && SUPPORTED_LANGUAGES.includes(segments[0])) segments.shift();
    return `/${segments.join('/')}`;
  }

  private decode(value: string): string {
    return value
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>');
  }

  private loadSite(): Observable<SiteData> {
    if (!this.site$) {
      const safe = (source: Observable<any>) => source.pipe(catchError(() => of(null)));
      this.site$ = forkJoin({
        company: safe(this.publicService.getCompanyDetails({filter: 'active_equals_true'})),
        about: safe(this.publicService.getAbout({filter: 'active_equals_true'})),
        networks: safe(this.publicService.getNetworks({filter: 'active_equals_true'})),
        languages: safe(this.publicService.getLanguages())
      }).pipe(
        map(({company, about, networks, languages}) => ({
          company: company?.data?.[0] ?? null,
          about: about?.data?.[0] ?? null,
          networks: networks?.data ?? [],
          languages: this.activeLanguages(languages?.data)
        })),
        shareReplay({bufferSize: 1, refCount: false})
      );
    }
    return this.site$;
  }

  private activeLanguages(data: any[] | undefined): string[] {
    const keys = (data || [])
      .filter(item => item?.active !== false)
      .map(item => String(item?.key || '').toLowerCase())
      .filter(key => SUPPORTED_LANGUAGES.includes(key));
    const unique = Array.from(new Set([DEFAULT_LANGUAGE, ...keys]));
    return unique;
  }

  private loadPageMeta(): Observable<any[]> {
    if (!this.pageMeta$) {
      this.pageMeta$ = this.publicService.getMeta({page: 1, rowsPerPage: 100}).pipe(
        map((response: any) => response?.data ?? []),
        catchError(() => of([])),
        shareReplay({bufferSize: 1, refCount: false})
      );
    }
    return this.pageMeta$;
  }
}
